import React, { useEffect, useState, useRef, memo } from 'react';
import { Users, CheckCircle2, XCircle, TrendingUp, ShieldCheck, Award, Star } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { VideoPlayer } from '../components/VideoPlayer';
import { BonusCarousel } from '../components/BonusCarousel';
import { LearningCarousel } from '../components/LearningCarousel';
import { FAQSection } from '../components/FAQSection';
import { ReviewForm } from '../components/ReviewForm';
import { ASSETS, PURCHASE_LINK, ALL_TESTIMONIALS } from '../data/landing-data';
export function LandingPage() {
  const [spotsLeft, setSpotsLeft] = useState(13);
  const [viewers, setViewers] = useState(32);
  const [showHeader, setShowHeader] = useState(false);
  const heroRef = useRef<HTMLDivElement>(null);
  const [currentDate, setCurrentDate] = useState('');
  const [visibleCount, setVisibleCount] = useState(5);
  // Scroll Reveal Logic - Fade In Down
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('visible');
        }
      });
    }, {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    });
    const elements = document.querySelectorAll('.scroll-reveal');
    elements.forEach(el => observer.observe(el));
    return () => elements.forEach(el => observer.unobserve(el));
  }, []);
  useEffect(() => {
    const interval = setInterval(() => {
      setSpotsLeft(prev => prev > 3 ? prev - 1 : 3);
    }, 45000);
    const viewerInterval = setInterval(() => {
      setViewers(prev => prev + Math.floor(Math.random() * 5) - 2);
    }, 5000);
    const handleScroll = () => {
      if (heroRef.current) {
        const heroBottom = heroRef.current.getBoundingClientRect().bottom;
        setShowHeader(heroBottom < 0);
      }
    };
    window.addEventListener('scroll', handleScroll);
    const date = new Date();
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      day: 'numeric',
      month: 'long'
    };
    setCurrentDate(date.toLocaleDateString('es-AR', options).toUpperCase());
    return () => {
      clearInterval(interval);
      clearInterval(viewerInterval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);
  const scrollToOffer = () => {
    const element = document.getElementById('offer-section');
    if (element) element.scrollIntoView({
      behavior: 'smooth'
    });
  };
  const loadMoreComments = () => {
    setVisibleCount(prev => Math.min(prev + 5, ALL_TESTIMONIALS.length));
  };
  return <div className="min-h-screen bg-black text-slate-200 font-sans selection:bg-orange-500/30" data-id="element-82">
      <style data-id="element-83">{`
        .scroll-reveal {
          opacity: 0;
          transform: translateY(-30px); /* Start from above (Fade In Down) */
          transition: opacity 1.0s ease-out, transform 1.0s ease-out;
        }
        .scroll-reveal.visible {
          opacity: 1;
          transform: translateY(0);
        }
      `}</style>

      {/* STICKY HEADER */}
      <header className={`fixed top-0 left-0 right-0 z-50 bg-black/95 backdrop-blur-md border-b border-blue-500/30 py-2 px-4 transition-transform duration-300 ${showHeader ? 'translate-y-0' : '-translate-y-full'}`} data-id="element-84">
        <div className="max-w-7xl mx-auto flex items-center justify-between" data-id="element-85">
          <img src={ASSETS.logo} alt="Moto Logic Pro" className="h-8 w-auto" data-id="element-86" />
          <Button size="sm" onClick={scrollToOffer} className="text-xs md:text-sm" data-id="element-87">
            VER OFERTA
          </Button>
        </div>
      </header>

      {/* 1. HERO SECTION */}
      <section ref={heroRef} className="relative pt-10 pb-6 px-0 sm:px-6 lg:px-8 overflow-hidden" data-id="element-88">
        <div className="absolute top-0 left-1/2 -translate-x-1/2 w-full h-[500px] bg-orange-500/10 blur-[120px] rounded-full pointer-events-none" data-id="element-89" />

        <div className="max-w-5xl mx-auto text-center relative z-10 px-4" data-id="element-90">
          <p className="scroll-reveal text-slate-300 font-light text-[10px] md:text-xs uppercase tracking-[0.2em] mb-2" data-id="element-91">
            7 MILLONES DE MOTOS EN ARGENTINA BUSCAN UN MECÁNICO DE CONFIANZA. SÉ
            VOS EL PRÓXIMO
          </p>

          {/* H1 */}
          <div className="scroll-reveal flex flex-col items-center gap-0 mb-4 leading-none" data-id="element-92">
            <span className="text-white text-lg md:text-2xl font-bold tracking-tight mb-1" data-id="element-93">
              CONVERTÍ TU PASIÓN EN UNA
            </span>
            <span className="text-[#FF7B22] text-3xl md:text-5xl font-black tracking-tighter uppercase relative mb-1" style={{
            textShadow: '0 2px 10px rgba(255,123,34,0.4)'
          }} data-id="element-94">
              FÁBRICA DE INGRESOS
            </span>
            <span className="text-white text-base sm:text-xl font-bold tracking-tight mt-1 mb-1" data-id="element-95">
              CON EL SISTEMA
            </span>
            <div className="flex items-baseline justify-center gap-2" data-id="element-96">
              <span className="text-[#D1D5DB] text-3xl md:text-5xl font-black tracking-tighter uppercase" style={{
              textShadow: '0 2px 10px rgba(209,213,219,0.2)'
            }} data-id="element-97">
                MOTO LOGIC
              </span>
              <span className="text-[#FF7B22] text-3xl md:text-5xl font-black tracking-tighter uppercase" style={{
              textShadow: '0 2px 10px rgba(255,123,34,0.3)'
            }} data-id="element-98">
                PRO
              </span>
            </div>
          </div>

          {/* Video - 90% width, centered */}
          <div className="scroll-reveal w-[90%] sm:max-w-3xl mx-auto mb-5 shadow-2xl shadow-orange-500/10" data-id="element-99">
            <VideoPlayer src={ASSETS.video} muted={true} autoPlay={true} loop={true} showSoundButton={false} data-id="element-100" />
          </div>

          {/* Social Proof Trust Badge */}
          <div className="scroll-reveal flex flex-col items-center justify-center gap-1 mb-3" data-id="element-101">
            <div className="flex gap-0.5" data-id="element-102">
              {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" data-id="element-103" />)}
              <span className="text-white text-xs font-bold ml-1" data-id="element-104">4.9/5</span>
            </div>
            <p className="text-slate-300 text-[10px] md:text-xs font-medium" data-id="element-105">
              +3.200 alumnos ya están facturando con este método
            </p>
          </div>

          {/* Text Paragraph - Big, bold, uppercase, tracking */}
          <p className="scroll-reveal text-white font-bold text-sm md:text-lg uppercase tracking-widest mb-5 max-w-3xl mx-auto px-4" data-id="element-106">
            TU SALIDA LABORAL ESTÁ A UN CLICK.
          </p>

          {/* CTA Button */}
          <div className="scroll-reveal px-4" data-id="element-107">
            <Button size="lg" fullWidth className="max-w-md mx-auto shadow-orange-500/25 py-3 text-sm" onClick={scrollToOffer} data-id="element-108">
              QUIERO ACCEDER A MOTO LOGIC PRO AHORA
            </Button>
          </div>
        </div>
      </section>

      {/* 2. PAIN POINTS */}
      <section className="py-8 bg-black/40 px-4" data-id="element-109">
        <div className="max-w-4xl mx-auto" data-id="element-110">
          <div className="scroll-reveal text-center mb-6" data-id="element-111">
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-2 uppercase" data-id="element-112">
              ¿Te pasa que amás las motos pero no te animás a dar el siguiente
              paso?
            </h2>
            <p className="text-base text-slate-400 max-w-2xl mx-auto" data-id="element-113">
              Sabemos que te apasiona el ruido del motor. Que te gusta meter
              mano. Pero también sabemos qué es lo que te detiene para empezar a
              vivir de esto:
            </p>
          </div>

          <div className="scroll-reveal grid md:grid-cols-3 gap-3 mb-6" data-id="element-114">
            {['El miedo a desarmar algo y no saber cómo volver a armarlo.', 'Sentir que te faltan años de estudio o herramientas carísimas para arrancar.', 'La inseguridad de no saber cuánto cobrar o cómo conseguir tus primeros clientes.'].map((pain, idx) => <Card key={idx} className="p-4 border-red-900/30 bg-red-950/10 hover:bg-red-950/20 transition-colors" data-id="element-115">
                <div className="flex flex-col items-center text-center gap-3" data-id="element-116">
                  <XCircle className="w-8 h-8 text-red-500" data-id="element-117" />
                  <p className="font-medium text-slate-200 text-xs" data-id="element-118">{pain}</p>
                </div>
              </Card>)}
          </div>

          <div className="scroll-reveal text-center bg-slate-800/30 p-4 rounded-xl border border-slate-700/50" data-id="element-119">
            <p className="text-sm font-medium text-white" data-id="element-120">
              <span className="text-[#FF7B22] font-bold" data-id="element-121">MOTO LOGIC PRO</span>{' '}
              nació para eliminar esas barreras. Te damos la seguridad técnica
              para que dejes de ser un aficionado y te conviertas en el
              profesional que tu zona está necesitando hoy mismo.
            </p>
          </div>
        </div>
      </section>

      {/* 3. OPPORTUNITY */}
      <section className="py-8 px-4" data-id="element-122">
        <div className="max-w-6xl mx-auto" data-id="element-123">
          <h2 className="scroll-reveal text-2xl md:text-3xl font-bold text-white text-center mb-6 uppercase" data-id="element-124">
            ¿Por qué la mecánica es tu mejor oportunidad en Argentina?
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-125">
            {[{
            icon: <TrendingUp className="w-5 h-5" data-id="element-126" />,
            title: 'DEMANDA INAGOTABLE',
            desc: 'La moto es el principal medio de transporte y trabajo en el país. El flujo de clientes es constante y masivo.'
          }, {
            icon: <ShieldCheck className="w-5 h-5" data-id="element-127" />,
            title: 'NEGOCIO RESISTENTE',
            desc: 'En contextos económicos difíciles, la gente prioriza reparar su vehículo antes que comprar uno nuevo. Tu trabajo siempre es prioridad.'
          }, {
            icon: <Award className="w-5 h-5" data-id="element-128" />,
            title: 'VALOR POR DIAGNÓSTICO',
            desc: "Lo que más se paga hoy no es 'apretar tornillos', es saber por qué falla la moto. El que diagnostica rápido y bien, gana más dinero."
          }, {
            icon: <Users className="w-5 h-5" data-id="element-129" />,
            title: 'FALTA DE EXPERTOS REALES',
            desc: 'Sobran improvisados, pero faltan mecánicos que dominen la técnica integral. Ese lugar vacío es tu oportunidad de oro.'
          }].map((item, idx) => <Card key={idx} className="scroll-reveal p-4 hover:-translate-y-1 transition-transform duration-300 bg-white/5 border-white/10" data-id="element-130">
                <div className="bg-orange-500/10 w-10 h-10 rounded-full flex items-center justify-center text-[#FF7B22] mb-3 mx-auto" data-id="element-131">
                  {item.icon}
                </div>
                <h3 className="text-sm font-bold text-white mb-1 text-center" data-id="element-132">
                  {item.title}
                </h3>
                <p className="text-slate-400 text-center text-xs leading-relaxed" data-id="element-133">
                  {item.desc}
                </p>
              </Card>)}
          </div>
        </div>
      </section>

      {/* 4. SOLUTION SECTION */}
      <section className="py-8 bg-black/40 px-4" data-id="element-134">
        <div className="max-w-5xl mx-auto" data-id="element-135">
          <div className="grid md:grid-cols-2 gap-6 items-center" data-id="element-136">
            <div className="md:col-span-2 text-center max-w-3xl mx-auto" data-id="element-137">
              <h2 className="scroll-reveal text-2xl md:text-3xl font-bold text-white mb-4 uppercase" data-id="element-138">
                ¿Qué vas a lograr con{' '}
                <span className="text-[#FF7B22]" data-id="element-139">Moto Logic Pro</span>?
              </h2>
              <ul className="space-y-3 mb-6 text-left inline-block" data-id="element-140">
                {[{
                title: 'MÉTODO DE LÓGICA DE TALLER',
                desc: 'No vas a estudiar de memoria. Vas a entender el funcionamiento real de cada componente para resolver cualquier falla con total precisión.'
              }, {
                title: 'INDEPENDENCIA LABORAL',
                desc: 'Olvidate de los jefes. Manejá tus propios horarios y trabajá desde tu propio garage o espacio disponible.'
              }, {
                title: 'INGRESOS DESDE EL PRIMER MES',
                desc: 'Te enseñamos las reparaciones más buscadas para que recuperes tu inversión casi de inmediato.'
              }, {
                title: 'CRECIMIENTO SIN TECHO',
                desc: 'Empezás con trabajos puntuales y escalás hasta tener tu propio taller reconocido en tu ciudad.'
              }].map((item, idx) => <li key={idx} className="scroll-reveal flex gap-2" data-id="element-141">
                    <CheckCircle2 className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" data-id="element-142" />
                    <div data-id="element-143">
                      <h4 className="font-bold text-white text-sm" data-id="element-144">
                        {item.title}
                      </h4>
                      <p className="text-slate-400 text-xs" data-id="element-145">{item.desc}</p>
                    </div>
                  </li>)}
              </ul>
              <div className="scroll-reveal mt-4" data-id="element-146">
                <Button size="lg" onClick={scrollToOffer} className="w-full md:w-auto py-3 text-sm" data-id="element-147">
                  QUIERO EMPEZAR MI CAMINO PROFESIONAL
                </Button>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* NEW SECTION: DE CERO A EXPERTO (CAROUSEL) */}
      <section className="py-8 px-4 border-t border-white/5" data-id="element-148">
        <div className="max-w-7xl mx-auto" data-id="element-149">
          <h2 className="scroll-reveal text-2xl md:text-3xl font-bold text-white text-center mb-6 uppercase" data-id="element-150">
            DE CERO A EXPERTO: TODO LO QUE VAS A APRENDER
          </h2>
          <div className="scroll-reveal" data-id="element-151">
            <LearningCarousel data-id="element-152" />
          </div>
        </div>
      </section>

      {/* 5. BONUSES */}
      <section className="py-8 bg-black/40 px-4 overflow-hidden" data-id="element-153">
        <div className="max-w-7xl mx-auto" data-id="element-154">
          <h2 className="scroll-reveal text-2xl md:text-3xl font-bold text-white text-center mb-6 uppercase" data-id="element-155">
            🎁 Si accedés hoy, te llevás estos{' '}
            <span className="text-[#FF7B22]" data-id="element-156">5 Bonus de Regalo</span>:
          </h2>
          <div className="scroll-reveal" data-id="element-157">
            <BonusCarousel data-id="element-158" />
          </div>
        </div>
      </section>

      {/* 6. OFFER SECTION */}
      <section id="offer-section" className="py-8 px-4 relative overflow-hidden" data-id="element-159">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/80" data-id="element-160" />
        <div className="max-w-3xl mx-auto relative z-10" data-id="element-161">
          <Card className="scroll-reveal p-5 sm:p-8 text-center border border-blue-500/50 shadow-[0_0_30px_-10px_rgba(59,130,246,0.3)] bg-[#0f0c29]/80 backdrop-blur" data-id="element-162">
            {/* Product Bundle Image - Integrated at top */}
            <div className="mb-3 -mt-3 md:-mt-5" data-id="element-163">
              <img src={ASSETS.productBundle} alt="Moto Logic Pro Bundle" className="w-full max-w-sm mx-auto object-contain drop-shadow-xl" data-id="element-164" />
            </div>

            <h2 className="text-xl md:text-2xl font-black text-white mb-4 uppercase leading-tight" data-id="element-165">
              Moto Logic Pro + 5 Bonus de Regalo
            </h2>

            <div className="mb-6 space-y-3" data-id="element-166">
              {/* Precio Tachado */}
              <div className="text-slate-400 text-lg font-medium flex items-center justify-center gap-2" data-id="element-167">
                <span data-id="element-168">ANTES</span>
                <span className="line-through decoration-red-500 decoration-2" data-id="element-169">
                  $59.900
                </span>
              </div>

              {/* Banner de Fecha Fijo */}
              <div className="bg-black border border-[#FF7B22] rounded-md py-2 px-4 mx-auto max-w-sm" data-id="element-170">
                <p className="text-[#FF7B22] font-bold text-xs md:text-sm uppercase tracking-wide" data-id="element-171">
                  🔥 OFERTA VÁLIDA SOLO POR HOY, {currentDate} ⚠️
                </p>
              </div>

              {/* Precio Final */}
              <div className="text-6xl md:text-7xl font-black text-[#00FF00] tracking-tight drop-shadow-lg" data-id="element-172">
                $19.990
              </div>

              {/* Estrellas */}
              <div className="flex items-center justify-center gap-1 text-yellow-400 font-bold mt-2" data-id="element-173">
                <div className="flex" data-id="element-174">
                  {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-4 h-4 fill-current" data-id="element-175" />)}
                </div>
                <span className="text-sm" data-id="element-176">4.9/5</span>
              </div>

              {/* Contadores Animados */}
              <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-4 bg-slate-900/80 px-4 py-2.5 rounded-xl border border-white/10 mx-auto max-w-md mt-3" data-id="element-177">
                <span className="flex items-center gap-2 text-xs font-bold" data-id="element-178">
                  <span className="relative flex h-2.5 w-2.5" data-id="element-179">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" data-id="element-180"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-green-500" data-id="element-181"></span>
                  </span>
                  <span className="text-white" data-id="element-182">
                    {viewers} personas viendo ahora
                  </span>
                </span>
                <span className="hidden sm:block text-slate-600" data-id="element-183">|</span>
                <span className="flex items-center gap-2 text-xs font-bold" data-id="element-184">
                  <span className="relative flex h-2.5 w-2.5" data-id="element-185">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75" data-id="element-186"></span>
                    <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-red-500" data-id="element-187"></span>
                  </span>
                  <span className="text-white" data-id="element-188">
                    Quedan {spotsLeft} cupos disponibles
                  </span>
                </span>
              </div>

              <p className="text-slate-400 font-medium text-xs pt-2" data-id="element-189">
                (Único pago - Acceso de por vida)
              </p>
            </div>

            <Button size="lg" fullWidth className="text-sm sm:text-base py-4 shadow-lg shadow-yellow-500/20 mb-6 bg-yellow-400 hover:bg-yellow-300 text-black border-transparent font-black" onClick={() => window.location.href = PURCHASE_LINK} data-id="element-190">
              QUIERO MI ACCESO AHORA
            </Button>

            {/* Payment - Mercado Pago large */}
            <div className="mb-6" data-id="element-191">
              <img src={ASSETS.mercadoPago} alt="Mercado Pago" className="w-[80%] max-w-xs mx-auto object-contain mb-6 rounded-md" data-id="element-192" />

              {/* Trust block - Rediseñado y más grande */}
              <div className="flex flex-col sm:flex-row gap-4 w-full justify-center items-stretch" data-id="element-193">
                {/* Sello Seguridad */}
                <div className="flex-1 flex flex-col items-center text-center gap-2 bg-black/40 p-4 rounded-xl" data-id="element-194">
                  <img src={ASSETS.compraSegura} alt="Compra Segura" className="h-12 object-contain drop-shadow-[0_0_10px_rgba(74,222,128,0.3)]" data-id="element-195" />
                  <p className="text-white font-bold text-sm uppercase mt-1" data-id="element-196">
                    PAGO 100% SEGURO
                  </p>
                  <p className="text-slate-400 text-xs leading-relaxed" data-id="element-197">
                    Tus datos están protegidos con encriptación bancaria. Compra
                    procesada de forma segura.
                  </p>
                </div>

                {/* Sello Garantía */}
                <div className="flex-1 flex flex-col items-center text-center gap-2 bg-black/40 p-4 rounded-xl" data-id="element-198">
                  <img src={ASSETS.garantia} alt="Garantía 7 Días" className="h-12 object-contain drop-shadow-[0_0_10px_rgba(250,204,21,0.3)]" data-id="element-199" />
                  <p className="text-white font-bold text-sm uppercase mt-1" data-id="element-200">
                    GARANTÍA DE 7 DÍAS
                  </p>
                  <p className="text-slate-400 text-xs leading-relaxed" data-id="element-201">
                    Si el material no es lo que esperabas, te devolvemos el 100%
                    de tu dinero sin preguntas.
                  </p>
                </div>
              </div>
            </div>
          </Card>
        </div>
      </section>

      {/* NEW SECTION: TOUR INTERNO (VERTICAL 9:16 VIDEO) */}
      <section className="py-8 bg-black/40 border-t border-white/5 overflow-hidden" data-id="element-202">
        <div className="w-full max-w-none text-center" data-id="element-203">
          <div className="scroll-reveal mb-4" data-id="element-204">
            <h2 className="text-xl md:text-2xl font-bold text-white mb-1 px-4" data-id="element-205">
              Mirá Todo el Contenido del Curso
            </h2>
            <p className="text-white/80 text-sm px-4" data-id="element-206">
              ⚡ Guías en video de alta precisión para diagnóstico y reparación
              inmediata + diagramas
            </p>
          </div>

          {/* Vertical Video Container - Full Width, Auto Height */}
          <div className="scroll-reveal w-full mx-auto relative overflow-hidden" data-id="element-207">
            <VideoPlayer src={ASSETS.tourVideo} loop={true} autoPlay={true} showSoundButton={false} // Disable sound button for this video
          className="w-full h-auto rounded-none bg-transparent border-none shadow-none" videoClassName="w-full h-auto block" data-id="element-208" />
          </div>
        </div>
      </section>

      {/* 7. TESTIMONIALS */}
      <section className="py-8 px-4" data-id="element-209">
        <div className="max-w-4xl mx-auto" data-id="element-210">
          {/* Reduced size by 50% (text-xl instead of 3xl/4xl) */}
          <div className="scroll-reveal text-center mb-3" data-id="element-211">
            <h2 className="text-base font-bold text-white mb-1 uppercase" data-id="element-212">
              MÁS DE 3.200 ALUMNOS SATISFECHOS
            </h2>
            {/* Unified Rating Row */}
            <div className="flex items-center justify-center gap-1 text-white text-[10px] font-bold" data-id="element-213">
              <span data-id="element-214">4.9/5 – 1.243 reseñas verificadas</span>
              <div className="flex gap-0.5" data-id="element-215">
                {[1, 2, 3, 4, 5].map(i => <Star key={i} className="w-2.5 h-2.5 text-yellow-500 fill-yellow-500" data-id="element-216" />)}
              </div>
            </div>
          </div>

          <div className="scroll-reveal space-y-2 mb-4" data-id="element-217">
            {ALL_TESTIMONIALS.slice(0, visibleCount).map((testimonial, idx) => <div key={idx} className="bg-white/5 border border-white/10 p-3 rounded-lg" data-id="element-218">
                <div className="flex gap-0.5 mb-1" data-id="element-219">
                  {[1, 2, 3, 4, 5].map(star => <Star key={star} className={`w-2.5 h-2.5 ${star <= (testimonial.rating || 5) ? 'text-yellow-500 fill-yellow-500' : 'text-slate-600 fill-slate-600'}`} data-id="element-220" />)}
                </div>
                <p className="text-slate-300 italic mb-1 text-xs" data-id="element-221">
                  "{testimonial.text}"
                </p>
                <p className="font-bold text-white text-[10px]" data-id="element-222">
                  {testimonial.name}
                </p>
              </div>)}
          </div>

          {visibleCount < ALL_TESTIMONIALS.length && <div className="scroll-reveal text-center" data-id="element-223">
              <Button size="sm" fullWidth onClick={loadMoreComments} data-id="element-224">
                CARGAR 5 COMENTARIOS MÁS
              </Button>
            </div>}

          <div className="scroll-reveal mt-4" data-id="element-225">
            <ReviewForm data-id="element-226" />
          </div>
        </div>
      </section>

      {/* 8. FAQ */}
      <div className="scroll-reveal" data-id="element-227">
        <FAQSection data-id="element-228" />
      </div>

      {/* Footer */}
      <footer className="py-4 bg-black/60 text-center text-slate-500 text-[10px] border-t border-white/5" data-id="element-229">
        <p data-id="element-230">© 2024 Moto Logic Pro. Todos los derechos reservados.</p>
        <p className="mt-0.5" data-id="element-231">
          Este sitio no es parte del sitio web de Facebook o Facebook Inc.
        </p>
      </footer>
    </div>;
}